import React, {useEffect, useState} from 'react'
import {useTranslation} from 'react-i18next'
import classNames from 'classnames'
import styles from 'components/Order/OrderDetails/OrderData.module.css'
import {ReadOnlyTextField} from 'components/Shared/ReadOnlyTextField'
import {fetchUser} from 'services'
import {CarrierDetails} from 'components/Order/OrderDetails/CarrierDetails'
import {useErrorHandling} from 'context/errorHandling'
import {HTTP_404_ERROR} from 'Constants'
import {fetchContractor, fetchContractorBalance} from 'services/ContractorService'
import {OrderPointsList} from './OrderPointsList'
import {AttachmentsData} from 'components/Order/OrderDetails/AttachmentsData'
import {scheduledEmailsFrequencyOptionFromEmailNotificationSettings} from 'components/Order/utils'
import Sections from 'components/Order/sections'
import Module from 'constants/customFieldModules'
import {DetailsColumn, DetailsRow} from 'components/Shared/CrudComponents'
import {CustomFieldsProvider} from 'context/customFields'
import {OrderTransactionsList} from 'components/Order/OrderDetails/OrderTransactionsList/OrderTransactionsList'
import {formatMerchantLimitMessage} from './Utils'
import {ReadOnlyEmailFieldSet} from 'components/Shared/ReadOnlyEmailFieldSet'
import {
  ORDER_ADDITIONAL_INFO_READ,
  ORDER_ATTACHMENTS_CARGO_PHOTOS_READ,
  ORDER_ATTACHMENTS_CONFIRMATION_READ,
  ORDER_ATTACHMENTS_DELIVERY_DOCUMENTS_READ,
  ORDER_ATTACHMENTS_OTHER_DOCUMENTS_READ,
  ORDER_CARRIER_READ,
  ORDER_CONTRACTOR_READ,
  ORDER_COST_READ,
  ORDER_DATA_READ,
  ORDER_DELIVERY_POINT_READ,
  ORDER_DISPATCHER_READ,
  ORDER_FORWARDER_READ,
  ORDER_INCOME_READ,
  ORDER_REPORT_SETTINGS_READ,
  useAuth
} from 'context/auth'
import {ReadOnlyCheckbox} from '../../Shared/ReadOnlyCheckbox'

export function OrderData({order, previousOrder, expandPointsList, attachmentsPreview, onClientDetails, onCarrierDetails, verificationWarnings}) {

  const [forwarder, setForwarder] = useState(null)
  const [dispatcher, setDispatcher] = useState(null)
  const [shareholder, setShareholder] = useState(null)
  const [client, setClient] = useState(null)
  const [payer, setPayer] = useState(null)
  const [clientBalance, setClientBalance] = useState(null)

  const [prevForwarder, setPrevForwarder] = useState(null)
  const [prevDispatcher, setPrevDispatcher] = useState(null)
  const [previousShareholder, setPreviousShareholder] = useState(null)
  const [prevClient, setPrevClient] = useState(null)
  const [prevPayer, setPrevPayer] = useState(null)
  const {hasAnyPrivileges} = useAuth()

  const hasOrderDataReadPermission = hasAnyPrivileges([ORDER_DATA_READ])
  const hasForwarderReadPermission = hasAnyPrivileges([ORDER_FORWARDER_READ])
  const hasDispatcherReadPermission = hasAnyPrivileges([ORDER_DISPATCHER_READ])
  const hasContractorReadPrivilege = hasAnyPrivileges([ORDER_CONTRACTOR_READ])
  const hasAdditionalInfoReadPrivileges = hasAnyPrivileges([ORDER_ADDITIONAL_INFO_READ])

  const {handleError} = useErrorHandling()

  const {t} = useTranslation()

  useEffect(() => {
    if (hasForwarderReadPermission) {
      fetchUser(order.forwarderId)
        .then(setForwarder)
        .catch(error => handleError(error, {
          [HTTP_404_ERROR]: 'notifications.error.forwarderNotFound'
        }))
      if (previousOrder && previousOrder.forwarderId !== order.forwarderId) {
        fetchUser(previousOrder.forwarderId)
          .then(setPrevForwarder)
          .catch(error => handleError(error, {
            [HTTP_404_ERROR]: 'notifications.error.forwarderNotFound'
          }))
      } else {
        setPrevForwarder(null)
      }
    }
  }, [order, previousOrder, hasForwarderReadPermission, handleError])

  useEffect(() => {
    if (hasDispatcherReadPermission) {
      if (order.dispatcherId) {
        fetchUser(order.dispatcherId)
          .then(setDispatcher)
          .catch(error => handleError(error, {
            [HTTP_404_ERROR]: 'notifications.error.dispatcherNotFound'
          }))
      } else {
        setDispatcher(null)
      }
      if (previousOrder?.dispatcherId && previousOrder.dispatcherId !== order.dispatcherId) {
        fetchUser(previousOrder.dispatcherId)
          .then(setPrevDispatcher)
          .catch(error => handleError(error, {
            [HTTP_404_ERROR]: 'notifications.error.dispatcherNotFound'
          }))
      } else {
        setPrevDispatcher(null)
      }
    }
  }, [order, previousOrder, hasDispatcherReadPermission, handleError])

  useEffect(() => {
    if (hasContractorReadPrivilege) {
      const clientId = order.contractorInfo.clientId
      const payerId = order.contractorInfo.payerId
      if (clientId) {
        fetchContractor(clientId)
          .then((client) => {
            setClient(client)
            if(client && onClientDetails){
              onClientDetails(client)
            }

          })
          .catch(error => handleError(error, {
            [HTTP_404_ERROR]: 'notifications.error.contractorNotFound'
          }))
      }
      if (payerId) {
        fetchContractorBalance(payerId)
          .then(setClientBalance)
          .catch(handleError)
      }
      if (previousOrder && previousOrder.contractorInfo.clientId !== clientId) {
        fetchContractor(previousOrder.contractorInfo.clientId)
          .then(setPrevClient)
          .catch(error => handleError(error, {
            [HTTP_404_ERROR]: 'notifications.error.contractorNotFound'
          }))
      } else {
        setPrevClient(null)
      }
      if (order.contractorInfo.payerId) {
        fetchContractor(order.contractorInfo.payerId)
          .then(setPayer)
          .catch(error => handleError(error, {
            [HTTP_404_ERROR]: 'notifications.error.contractorNotFound'
          }))
      }
      if (previousOrder && previousOrder.contractorInfo.payerId !== order.contractorInfo.payerId) {
        fetchContractor(previousOrder.contractorInfo.payerId)
          .then(setPrevPayer)
          .catch(error => handleError(error, {
            [HTTP_404_ERROR]: 'notifications.error.contractorNotFound'
          }))
      } else {
        setPrevPayer(null)
      }
    }
  }, [order, previousOrder, hasContractorReadPrivilege,onClientDetails, handleError])

  useEffect(() => {
    if (hasAdditionalInfoReadPrivileges) {
      if (order.shareholderId) {
        fetchUser(order.shareholderId)
          .then(setShareholder)
          .catch(error => handleError(error, {
            [HTTP_404_ERROR]: 'notifications.error.forwarderNotFound'
          }))
      } else {
        setShareholder(null)
      }
      if (previousOrder?.shareholderId && previousOrder.shareholderId !== order.shareholderId) {
        fetchUser(previousOrder.shareholderId)
          .then(setPreviousShareholder)
          .catch(error => handleError(error, {
            [HTTP_404_ERROR]: 'notifications.error.forwarderNotFound'
          }))
      } else {
        setPreviousShareholder(null)
      }
    }
  }, [order, previousOrder, hasAdditionalInfoReadPrivileges, handleError])

  return (order && <div>
    <CustomFieldsProvider module={Module.Orders} entityId={order?.id}>
      <div className={classNames(styles.detailsRow, styles.careProvidersBox)}>
        {hasForwarderReadPermission && forwarder &&
          <ReadOnlyTextField label={'order.details.forwarder'}
                             value={forwarder.firstName + ' ' + forwarder.lastName}
                             previousValue={prevForwarder && {value: prevForwarder.firstName + ' ' + prevForwarder.lastName}}/>
        }
        {hasDispatcherReadPermission && (dispatcher || prevDispatcher) &&
          <ReadOnlyTextField label={'order.details.dispatcher'}
                             value={dispatcher && (dispatcher.firstName + ' ' + dispatcher.lastName)}
                             previousValue={prevDispatcher &&
                               {value: prevDispatcher.firstName + ' ' + prevDispatcher.lastName}}/>}
      </div>
      <DetailsRow>
        <DetailsColumn section={Sections.ClientData} visible={hasContractorReadPrivilege || hasOrderDataReadPermission}>
          {hasOrderDataReadPermission && [
            <ReadOnlyTextField label={'order.details.readableId'}
                               value={order.readableId}
                               previousValue={previousOrder && {value: previousOrder.readableId}}/>,
            <ReadOnlyTextField label={'order.details.referenceId'}
                               value={order.referenceId}
                               previousValue={previousOrder && {value: previousOrder.referenceId}}/>]}
          {hasContractorReadPrivilege && [
            <ReadOnlyTextField label={'order.details.contractor.client'}
                               value={client?.fullName}
                               previousValue={prevClient && {value: prevClient.fullName}}/>,
            <ReadOnlyTextField label={'order.details.contractor.payer'}
                               value={payer?.fullName}
                               previousValue={prevPayer && {value: prevPayer.fullName}}
                               helperText={formatMerchantLimitMessage(payer, clientBalance, t)}/>,
            <ReadOnlyEmailFieldSet
              value={order.contractorInfo.emails}
              previousValue={previousOrder && {value: previousOrder.contractorInfo.emails}}
              label="order.details.contractor.email"/>,
            <ReadOnlyEmailFieldSet
              value={order.contractorInfo.bccEmails}
              previousValue={previousOrder && {value: previousOrder.contractorInfo.bccEmails}}
              label="order.details.contractor.bccEmail"/>,
            <ReadOnlyCheckbox
              checked={client?.requireCarrierVerifiedHV}
              diffValue={prevClient?.requireCarrierVerifiedHV}
              label={t('order.details.contractor.requireCarrierVerifiedHV')}/>]}
        </DetailsColumn>
        <DetailsColumn section={Sections.Carrier} visible={hasAnyPrivileges([ORDER_CARRIER_READ])}>
          <CarrierDetails value={order.deliveryMethod} previousValue={previousOrder?.deliveryMethod} onCarrierDetails={onCarrierDetails} carrierVerificationWarnings={verificationWarnings}/>
        </DetailsColumn>
        <DetailsColumn section={Sections.OrderInfo} visible={hasAdditionalInfoReadPrivileges}>
          <ReadOnlyTextField label={'order.details.comment'}
                             value={order.comment}
                             multiline
                             previousValue={previousOrder && {value: previousOrder.comment}}/>
          <ReadOnlyTextField label={'order.details.printComment'}
                             value={order.printComment}
                             multiline
                             previousValue={previousOrder && {value: previousOrder.printComment}}/>
          {(shareholder || previousShareholder) && <ReadOnlyTextField label={'order.details.shareholder'}
                                                                      value={shareholder && (shareholder.firstName + ' ' + shareholder.lastName)}
                                                                      previousValue={previousShareholder &&
                                                                        {value: previousShareholder.firstName + ' ' + previousShareholder.lastName}}/>}
          <ReadOnlyCheckbox
            checked={order.highValue}
            diffValue={previousOrder && previousOrder.highValue}
            label={t('order.details.highValue')}/>
        </DetailsColumn>
        <DetailsColumn section={Sections.Attachments}
                       visible={hasAnyPrivileges([
                         ORDER_ATTACHMENTS_DELIVERY_DOCUMENTS_READ,
                         ORDER_ATTACHMENTS_CARGO_PHOTOS_READ,
                         ORDER_ATTACHMENTS_OTHER_DOCUMENTS_READ,
                         ORDER_ATTACHMENTS_CONFIRMATION_READ])}>
          {attachmentsPreview && <div className={styles.attachmentsWrapper}>
            <AttachmentsData order={order} attachmentsPreview={attachmentsPreview}/>
          </div>}
        </DetailsColumn>
      </DetailsRow>
      {hasAnyPrivileges([ORDER_COST_READ, ORDER_INCOME_READ]) &&
        <>
          <div className={styles.header}>
            {t('form.order.transaction.title')}
          </div>
          <OrderTransactionsList
            transactions={order.transactions}/>
        </>
      }
      {hasAnyPrivileges([ORDER_DELIVERY_POINT_READ]) &&
        <>
          <div className={styles.header}>
            {t('order.details.order')}
          </div>
          <OrderPointsList
            order={order}
            previousOrder={previousOrder}
            expandPointsList={expandPointsList}/>
        </>
      }
      {hasAnyPrivileges([ORDER_REPORT_SETTINGS_READ]) &&
        <div className={classNames(styles.detailsRow, styles.emailBox)}>
          <div>
            {t('order.details.emailNotificationSettings.autoUpdate')}
          </div>
          <div>
            <ReadOnlyTextField

              label={'order.details.emailNotificationSettings.frequency'}
              value={t(scheduledEmailsFrequencyOptionFromEmailNotificationSettings(order.emailNotificationSettings).label)}
              previousValue={previousOrder && {value: t(scheduledEmailsFrequencyOptionFromEmailNotificationSettings(previousOrder.emailNotificationSettings).label)}}
            />
          </div>
          <div>
            <ReadOnlyTextField

              label={'order.details.emailNotificationSettings.language'}
              value={order.emailNotificationSettings.language}
              previousValue={previousOrder && {value: previousOrder.emailNotificationSettings.language}}
            />
          </div>
        </div>
      }
    </CustomFieldsProvider>
  </div>)
}

